import { Button, ChartLineIcon, LoadingIcon, Popover, Rectangle, Skeleton } from '@lokalise/louis'
import { useNavigate } from 'react-router-dom'
import { getNewEvaluationsUrl, getProfileUrl } from '../../../../utils/url'
import styles from './EvaluationsButton.module.css'
import { getDifferenceInDays } from './utils/getDifferenceInDays'

type EvaluationsButtonProps = {
  isInProgress: boolean
  hasEvaluations: boolean
  profileId: string
  evaluationsLimit?: number
  evaluationsRenewalDate?: string
  isAtEvaluationsLimit?: boolean
  isLoadingEvaluations?: boolean
}

export const EvaluationsButton = ({
  isInProgress,
  hasEvaluations,
  profileId,
  evaluationsLimit,
  evaluationsRenewalDate,
  isAtEvaluationsLimit,
  isLoadingEvaluations,
}: EvaluationsButtonProps) => {
  const navigate = useNavigate()

  if (isInProgress) {
    return (
      <Button disabled size="sm" variant="secondary">
        <div className="flex gap-1 items-center">
          <LoadingIcon size="12px" /> Evaluating
        </div>
      </Button>
    )
  }

  if (hasEvaluations) {
    return (
      <Button onClick={() => navigate(getProfileUrl(profileId))} size="sm" variant="secondary">
        <div className="flex gap-1 items-center">
          <ChartLineIcon size="12px" /> See metrics
        </div>
      </Button>
    )
  }

  // Know that profiles request has resolved, but it's possible the team evals request hasn't
  if (isLoadingEvaluations) {
    return (
      <Skeleton animated description="Loading...">
        <Rectangle width={125} height={30} />
      </Skeleton>
    )
  }

  if (isAtEvaluationsLimit) {
    const daysToRenew = getDifferenceInDays(evaluationsRenewalDate)

    return (
      <Popover
        content={
          <p className={styles.tooltip}>
            You have used all evaluations available for this month. You can wait {daysToRenew} for
            your quota to reset, or{' '}
            <a href="/team#billing" target="_blank" rel="noreferrer">
              upgrade your plan
            </a>{' '}
            to access additional evaluations.
          </p>
        }
        enterWithoutClosing
        triggerEvent="hover"
        withArrow
      >
        <Button disabled size="sm" variant="secondary">
          <div className="flex gap-1 items-center">
            <ChartLineIcon size="12px" /> {`${evaluationsLimit}/${evaluationsLimit}`} used
          </div>
        </Button>
      </Popover>
    )
  }

  return (
    <Button onClick={() => navigate(getNewEvaluationsUrl(profileId))} size="sm" variant="secondary">
      <div className="flex gap-1 items-center">
        <ChartLineIcon size="12px" /> Evaluate
      </div>
    </Button>
  )
}
